/**
 * HelpOverlay — renders the two visual help layers (copy from helpContent config):
 *   • TOUR: when helpStore.tourStep !== null, a dimmed backdrop + a highlight ring
 *     around the current anchor + a card (title/body/Step X of Y/Back/Next/Done).
 *   • HELP MODE: when helpStore.helpMode is on, a small "?" badge sits on every
 *     anchor; hovering it reveals the full rich card (the "rich hover cards").
 *
 * Mounted once at the app root (AppShell). Anchors are located by CSS selector
 * from helpContent.walkthrough; anchors not currently in the DOM are skipped.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useHelpStore } from '@/stores/helpStore';
import { walkthrough, type WalkthroughStep } from '@/config/helpContent';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function rectFor(selector: string): Rect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const CARD_W = 288;

function cardStyle(rect: Rect, placement: WalkthroughStep['placement']): CSSProperties {
  const gap = 12;
  let top = rect.top;
  let left = rect.left;
  const style: CSSProperties = { position: 'fixed', width: CARD_W, zIndex: 1001 };
  switch (placement) {
    case 'bottom':
      top = rect.top + rect.height + gap;
      break;
    case 'top':
      top = rect.top - gap;
      style.transform = 'translateY(-100%)';
      break;
    case 'right':
      top = rect.top;
      left = rect.left + rect.width + gap;
      break;
    case 'left':
      top = rect.top;
      left = rect.left - gap;
      style.transform = 'translateX(-100%)';
      break;
  }
  style.top = Math.max(8, top);
  style.left = Math.max(8, Math.min(left, window.innerWidth - CARD_W - 8));
  return style;
}

/** Force a re-render while the overlay is active so anchor rects stay current. */
function useLiveRects(active: boolean): void {
  const [, force] = useState(0);
  useEffect(() => {
    if (!active) return;
    const on = () => force((n) => n + 1);
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    const id = window.setInterval(on, 500);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
      window.clearInterval(id);
    };
  }, [active]);
}

function TourLayer() {
  const tourStep = useHelpStore((s) => s.tourStep) as number;
  const nextStep = useHelpStore((s) => s.nextStep);
  const prevStep = useHelpStore((s) => s.prevStep);
  const endTour = useHelpStore((s) => s.endTour);
  const step = walkthrough[tourStep];
  const rect = rectFor(step.selector);
  const isLast = tourStep >= walkthrough.length - 1;

  return (
    <div data-testid="help-tour" className="fixed inset-0 z-[1000]">
      {/* Dimmed backdrop (blocks interaction with the app while touring). */}
      <div className="absolute inset-0 bg-black/50" />
      {/* Highlight ring around the anchor (if it's on screen). */}
      {rect && (
        <div
          className="absolute rounded ring-2 ring-amber-400 ring-offset-2 ring-offset-transparent pointer-events-none"
          style={{
            position: 'fixed',
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.0)',
          }}
        />
      )}
      {/* Step card. Centered if the anchor isn't currently visible. */}
      <div
        data-testid="help-tour-card"
        className="bg-vibe-bg-secondary border border-vibe-border rounded-lg shadow-2xl p-4 text-sm"
        style={
          rect
            ? cardStyle(rect, step.placement)
            : {
                position: 'fixed',
                width: CARD_W,
                zIndex: 1001,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }
        }
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-vibe-text">{step.title}</h3>
          <button
            data-testid="help-tour-close"
            onClick={endTour}
            aria-label="Close tour"
            className="text-vibe-text-secondary hover:text-vibe-text -mt-1 -mr-1 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-vibe-text-secondary leading-snug">{step.body}</p>
        {!rect && (
          <p className="mt-2 text-xs text-vibe-text-secondary/70 italic">
            (This control isn't on screen right now — switch to the Designer view to see it.)
          </p>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-vibe-text-secondary">
            Step {tourStep + 1} of {walkthrough.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              data-testid="help-tour-prev"
              onClick={prevStep}
              disabled={tourStep === 0}
              className="flex items-center gap-1 px-2 py-1 rounded text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary disabled:opacity-40"
            >
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <button
              data-testid="help-tour-next"
              onClick={nextStep}
              className="flex items-center gap-1 px-3 py-1 rounded bg-vibe-accent text-white hover:bg-vibe-accent-hover"
            >
              {isLast ? 'Done' : 'Next'}
              {!isLast && <ChevronRight className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HelpModeBadge({ step }: { step: WalkthroughStep }) {
  const [hover, setHover] = useState(false);
  const rect = rectFor(step.selector);
  if (!rect) return null;
  return (
    <>
      <button
        data-testid={`help-badge-${step.id}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="pointer-events-auto absolute flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 text-black text-[11px] font-bold shadow ring-2 ring-vibe-bg"
        style={{ position: 'fixed', top: rect.top - 8, left: rect.left - 8, zIndex: 951 }}
        aria-label={`Help: ${step.title}`}
      >
        ?
      </button>
      {hover && (
        <div
          data-testid={`help-card-${step.id}`}
          className="pointer-events-none bg-vibe-bg-secondary border border-vibe-border rounded-lg shadow-2xl p-3 text-sm"
          style={cardStyle(rect, step.placement)}
        >
          <h3 className="font-semibold text-vibe-text mb-0.5">{step.title}</h3>
          <p className="text-vibe-text-secondary leading-snug">{step.body}</p>
        </div>
      )}
    </>
  );
}

function HelpModeLayer() {
  return (
    <div data-testid="help-mode-layer" className="fixed inset-0 z-[950] pointer-events-none">
      {walkthrough.map((step) => (
        <HelpModeBadge key={step.id} step={step} />
      ))}
    </div>
  );
}

export function HelpOverlay() {
  const tourStep = useHelpStore((s) => s.tourStep);
  const helpMode = useHelpStore((s) => s.helpMode);
  const active = tourStep !== null || helpMode;
  useLiveRects(active);

  if (tourStep !== null) return <TourLayer />;
  if (helpMode) return <HelpModeLayer />;
  return null;
}
